import {
	CODE_DIGITS_NUM, ERemoteModels, ECodeTypes, EUserCompatLevel, MAX_RC_BUTTONS
} from '../conf/board-users';
import { createHexString, parseHexString } from 'src/app/modules/shared/utils/strings';
import { compatibilityMap, EAutomationClass, ESwingGatesBoardId } from '../conf/automation-class';

export class BoardUser {
	Code?: string;
	Position?: number;
	Order?: number;
	Type: number;
	Mode?: number;
	State?: number;
	TypeDescription: string;
	Name?: string;
	GroupName?: string;
	Used: boolean;
	CodeBase?: string;
	isGroupHeader?: boolean;
	GroupId?: number;
	DateFrom?: string;
	DateTo?: string;
	Autom1Cmd?: number;
	Autom2Cmd?: number;
	Autom3Cmd?: number;
	Autom4Cmd?: number;
	functionIdList: number[];
	nameStr?: string;
}

export class BoardUserDelete {
	Code?: string;
	Position?: number;
}

export const buttonNumber = (bu: BoardUser): number => {
	let cc: number = codeChannel(bu);
	// 20180326: I assume each RC has a max number of buttons...
	if (cc > MAX_RC_BUTTONS - 1) {
		return -1;
	}

	// WARNING! This will not work if the codeTypeDescr is not correctly set!
	if (isNewTopRollingCode2ChanRCModel(bu.TypeDescription) && cc === 3) {
		cc = 1;
	}
	return (cc + 1);
};

export const twinModel = (bu: BoardUser): boolean => {
	return [ERemoteModels.TW2EE.toString(), ERemoteModels.TW4EE.toString()].includes(
		bu.TypeDescription
	);
};

export const ttsModel = (bu: BoardUser): boolean => {
	return bu.TypeDescription === ERemoteModels.TTS44FKS;
};

export const topSportModel = (bu: BoardUser): boolean => {
	return [ERemoteModels.TOP42FGN.toString(), ERemoteModels.TOP44FGN.toString(), ERemoteModels.TOPD4FRN.toString()].includes(
		bu.TypeDescription
	);
};

export const getBoardUserIcon = (bu: BoardUser, types: any[], isCodeGroup: boolean): string => {
	let buTypeName = '';
	if (ttsModel(bu)) {
		types = types.filter(buType => buType.Name !== ERemoteModels.TYPE_TWIN);
	} else if (twinModel(bu)) {
		types = types.filter(buType => buType.Name !== ERemoteModels.TYPE_TTS_FIXED_CODE);
	} else if (topSportModel(bu)) {
		types = types.filter(buType => buType.Name !== ERemoteModels.TYPE_TWIN);
	}
	const buType = types.find(b => b.Id === bu.Type);
	if (bu !== undefined && buType !== undefined) {
		buTypeName = buType.Name;
	}
	if (isCodeGroup && isCodeTypeGroupable(bu.Type)) {
		const buttonN = buttonNumber(bu);
		if (buttonN > 0) {
			return 'cm-button-' + buttonN;
		}
	}
	switch (bu.Type) {
		case ECodeTypes.CODE_TYPE_KEYBOARD:
			return 'cm-keypad';
		case ECodeTypes.CODE_TYPE_UNKNOWN:
		case ECodeTypes.CODE_TYPE_TRANSPONDER:
			return 'cm-card';
		case ECodeTypes.CODE_TYPE_TOP:
			return 'cm-radio-control-top';
		case ECodeTypes.CODE_TYPE_TAM:
			return 'cm-radio-control-tam';
		case ECodeTypes.CODE_TYPE_ROLLING:
			return 'cm-radio-control-rolling-code';
		case ECodeTypes.CODE_TYPE_TWIN_TTS_FIXED:
			if (buTypeName === 'Twin') {
				return 'cm-radio-control-twin';
			}
			return 'cm-radio-control-fix-code';
		case ECodeTypes.CODE_TYPE_KLT:
			return 'cm-devices';
		case ECodeTypes.CODE_TYPE_STRAUSS_KLT:
			return 'cm-devices';
		default: // defaults to code Type Remote controller for other types
			return 'cm-radio-control-fix-code';
	}
};

const codeChannel = (bu: BoardUser): number => {
	if (bu.Type === ECodeTypes.CODE_TYPE_TWIN_TTS_FIXED || bu.Type === ECodeTypes.CODE_TYPE_TAM || bu.Type === ECodeTypes.CODE_TYPE_TOP) {
		const codeTmp: string = bu.Code.substring(2);
		const byteArray = parseHexString(codeTmp);
		if (byteArray.length > 0) {
			return byteArray[byteArray.length - 1] & 3;
		}
	}

	// Convert from hex string to number
	const ret = parseInt(bu.Code.substring(0, 2), 16);
	return typeof ret === 'number' ? ret : -1;
};

// WARNING: Rolling codes are always groupable,
// fixed codes (TOP AND TAM) and TWIN are groupable only when they have their factory codes.
// If they are cloned then they can't be grouped!
export const isCodeTypeGroupable = (buType: number): boolean => (buType === ECodeTypes.CODE_TYPE_KLT ||
	buType === ECodeTypes.CODE_TYPE_STRAUSS_KLT ||
	buType === ECodeTypes.CODE_TYPE_TOP ||
	buType === ECodeTypes.CODE_TYPE_TAM ||
	buType === ECodeTypes.CODE_TYPE_ROLLING ||
	buType === ECodeTypes.CODE_TYPE_TWIN_TTS_FIXED)
	;

export const isRollingCode = (buType: number): boolean => !(buType === ECodeTypes.CODE_TYPE_UNKNOWN ||
	buType === ECodeTypes.CODE_TYPE_KEYBOARD ||
	buType === ECodeTypes.CODE_TYPE_TRANSPONDER
);

export const isBadge = (buType: number): boolean => buType === ECodeTypes.CODE_TYPE_TRANSPONDER;

const isNewTopRollingCode2ChanRCModel = (buTypeDescription: string): boolean => (buTypeDescription === ERemoteModels.TOPD2RGS ||
	buTypeDescription === ERemoteModels.TOPD2RBS ||
	buTypeDescription === ERemoteModels.TOPD2RKS ||
	buTypeDescription === ERemoteModels.TOPD2RYS ||
	buTypeDescription === ERemoteModels.TOPD2RES ||
	buTypeDescription === ERemoteModels.TOPD2RPS);

export const compareCodebase = (current: BoardUser, other: BoardUser): boolean => {
	const codeBase1 = codeBase(current);
	const codeBase2 = codeBase(other);
	return codeBase1.toLowerCase() === codeBase2.toLowerCase();
};

export const codeBase = (bu: BoardUser): string => {
	let codeTmp: string = bu.Code.substring(2);
	if (bu.Type === ECodeTypes.CODE_TYPE_TWIN_TTS_FIXED || bu.Type === ECodeTypes.CODE_TYPE_TAM || bu.Type === ECodeTypes.CODE_TYPE_TOP) {
		const byteArray = parseHexString(codeTmp);

		if (byteArray.length > 0) {
			// Shift right and then left of 2 bits to reset them...
			// eslint-disable-next-line no-bitwise
			byteArray[byteArray.length - 1] = byteArray[byteArray.length - 1] >> 2;
			// eslint-disable-next-line no-bitwise
			byteArray[byteArray.length - 1] = byteArray[byteArray.length - 1] << 2;

			codeTmp = createHexString(byteArray);
		}
	}

	return codeTmp;
};

// Use only to group to resolve error
export const codeBaseOld = (bu: BoardUser): string => {
	let codeTmp: string = bu.Code.substring(2);
	if (bu.Type === ECodeTypes.CODE_TYPE_TWIN_TTS_FIXED) {
		const byteArray = parseHexString(codeTmp);

		if (byteArray.length > 0) {
			// Shift right and then left of 2 bits to reset them...
			// eslint-disable-next-line no-bitwise
			byteArray[byteArray.length - 1] = byteArray[byteArray.length - 1] >> 2;
			// eslint-disable-next-line no-bitwise
			byteArray[byteArray.length - 1] = byteArray[byteArray.length - 1] << 2;

			codeTmp = createHexString(byteArray);
		}
	}

	return codeTmp;
};


export interface BoardUserCodeType {
	Id: number;
	Name: string;
	Items: string[];
}

export class BoardUserNode {
	name?: string;
	data?: BoardUser;
	level?: number;
	expandable?: boolean;
	children?: BoardUserNode[] = [];
	icon?: string;
	childrenLength?: number;
	isCodeGroup = false;

	constructor(boardUser: BoardUser) {
		this.data = boardUser;
		this.data.nameStr = boardUser.GroupName !== undefined && boardUser.GroupName !== null && boardUser.GroupName !== '' ? boardUser.GroupName : boardUser.Name;
		if (this.data.nameStr === undefined || this.data.nameStr === null) {
			this.data.nameStr = '';
		}
		this.data.functionIdList = listintingFunction(boardUser);
	}
}


const listintingFunction = (bu: BoardUser): number[] => {
	const listFu = [];
	let mode = bu.Mode;
	for (let i = 7; i >= 4; i--) {
		if (Math.floor(mode / (2 ** i)) === 1) {
			listFu.unshift(2 ** i);
			mode = mode - (2 ** i);
		}
	}
	if (bu.Autom1Cmd !== null) {
		listFu.push(bu.Autom1Cmd);
	}
	if (bu.Autom2Cmd !== null) {
		listFu.push(bu.Autom2Cmd);
	}
	if (bu.Autom3Cmd !== null) {
		listFu.push(bu.Autom3Cmd);
	}
	if (bu.Autom4Cmd !== null) {
		listFu.push(bu.Autom4Cmd);
	}
	return listFu;
};

export const boardusersListCompatibility = (
	fileContent: BoardUser[], aclass: number, boardId: number,
	currAclass: number, currBoardId: number, automationModes
): number => {
	const tmpBuListContentKeeper = fileContent;
	const fileCompatibility: number = getCompatibility(currAclass, currBoardId, aclass, boardId);
	if (fileCompatibility === EUserCompatLevel.Almost) {
		const currBuDefaultMode: number = getBoardUsersDefaultMode(automationModes);
		for (const boardUser of tmpBuListContentKeeper) {
			boardUser.Mode = currBuDefaultMode; // Re-assign the map to update the original entry...
		}
	}
	return fileCompatibility;
};

const getBoardUsersDefaultMode = (automationModes: any[]): number => {
	const items = automationModes.sort((a, b) => a.Value - b.Value);
	const defaultItem = items.find(i => i.Value > 0);
	return defaultItem.Value;
};

const getCompatibility = (currAclass: number, currBoardId: number, receivedAclass: number, receivedBoardId: number): number => {
	let fileCompatibility: number = EUserCompatLevel.Compatible;

	const currBoardCompatible = getCompatibleBoard(currAclass, currBoardId);
	const receivedBoardCompatible = getCompatibleBoard(receivedAclass, receivedBoardId);
	// If the current automation is a SHUTTER and we are loading something that is not from a SHUTTER then it will be not compatible...
	// This is due to the particular way SHUTTERS handle BU
	if ((currAclass === EAutomationClass.CLASS_SHUTTER && receivedAclass !== EAutomationClass.CLASS_SHUTTER) ||
		(currAclass !== EAutomationClass.CLASS_SHUTTER && receivedAclass === EAutomationClass.CLASS_SHUTTER) ||
		(currBoardCompatible && !receivedBoardCompatible)) {
		fileCompatibility = EUserCompatLevel.Incompatible;
	} else if (!sameControlItem(currAclass, currBoardId, receivedAclass, receivedBoardId)) {
		fileCompatibility = EUserCompatLevel.Almost;
	}
	return fileCompatibility;
};

const getCompatibleBoard = (aclass, boardId): boolean => aclass === EAutomationClass.CLASS_SWING_GATE &&
	(boardId === ESwingGatesBoardId.BOARD_ID_ZLJ24 || boardId === ESwingGatesBoardId.BOARD_ID_ZM3);

const sameControlItem = (aclass1: number, boardId1: number, aclass2: number, boardId2: number): boolean => {
	const ci1 = compatibilityMap.get(aclass1 + '-' + boardId1);
	const ci2 = compatibilityMap.get(aclass2 + '-' + boardId2);
	return ci1 === ci2;
};

export const kbCodeToHumanReadable = (code: string): string => {
	// HEX > DECIMAL
	// Example 00 00 00 00 0A 09 06 04 0A (keyboard code 09640)
	// A real "0" digit is represented by "0A"
	let tmpCode = code;

	// Remove all the initial bytes that are set to 00
	// while( tmpCode.startsWith("00") )
	// tmpCode.remove(0, 2);

	// Replace all the "0" with an empty string...
	tmpCode = tmpCode.replace(/0/g, '');
	// Replace (case insensitive) all the "A" with a "0"...
	tmpCode = tmpCode.replace(/A/gi, '0');

	return tmpCode;
};

export const humanReadableToKbCode = (code: string): string => {
	// DECIMAL > HEX
	// Example 00 00 00 00 0A 09 06 04 0A (keyboard code 09640)
	// A real "0" digit is represented by "0A"

	// In case of empty string do not make any modification...this is an invalid code...
	if (code.length <= 0) {
		return '';
	}

	// Replace (case insensitive) all the "0" with a "A"...
	const tmpCode = code.replace(/0/g, 'A');

	// Add "0" before each digit
	let tmpCode2 = '';

	// eslint-disable-next-line @typescript-eslint/prefer-for-of
	for (let i = 0; i < tmpCode.length; i++) {
		tmpCode2 += '0' + tmpCode[i];
	}

	// Append the remaining zeroes
	const remSize = (CODE_DIGITS_NUM - tmpCode2.length);
	for (let i = 0; i < remSize; i++) {
		tmpCode2 = '0' + tmpCode2; // prepend 0
	}

	return tmpCode2;
};
